import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";

import {
  DEFAULT_LIST_LIMIT,
  RunSequence,
  State,
} from "react-lib/apps/MEH/sequence/WellnessEmployeeManagement";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";
import formatComma from "react-lib/utils/formatComma";
import { Table } from "react-lib/frameworks/Table";
import { adToBe, formatDateDashToSlash, formatDatetime } from "react-lib/utils/dateUtils";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Header,
  Icon,
  Input,
  Modal,
  Popup,
  Tab,
  TabProps,
  TextArea,
} from "semantic-ui-react";

// UX

// Utils
import { useIntl } from "react-intl";
import { DateTextBox } from "react-lib/apps/common";

// CSS
import "./CardWellnessEmployeeManagement.scss";

// Types
export type CardWellnessEmployeeManagementProps = {
  setProp: (key: string, value: any) => any;
  onEvent: (e: any) => any;
  // seq
  runSequence: RunSequence;
  // data
  WellnessEMSequence: State["WellnessEMSequence"];
  // CommonInterface
  buttonLoadCheck?: Record<string, any>;
  errorMessage?: Record<string, any>;
  successMessage?: Record<string, any>;
  masterData?: Record<string, any[]>;
  masterOptions?: Record<string, any[]>;
  divisionHasUserItems?: Record<string, any>[];
  selectedDivision?: Record<string, any>;
  languageUX?: string;
};

const CARD_WELLNESS_EMPLOYEE_MANAGEMENT = "CardWellnessEmployeeManagement";

const CardWellnessEmployeeManagement = (props: CardWellnessEmployeeManagementProps) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  useEffect(() => {
    props.runSequence({
      sequence: "WellnessEmployeeManagement",
      action: "SEARCH",
      card: CARD_WELLNESS_EMPLOYEE_MANAGEMENT,
      restart: true,
    });
  }, [props.selectedDivision?.id]);

  const updateEditedItems = (updatedItems: any[], index1: number) => {
    const editItems = props.WellnessEMSequence?.wellnessPPHDList?.editItems || [];

    const editedItem = updatedItems[index1];

    const updatedEditItems = editItems.some((item: any) => item.id === editedItem.id)
      ? editItems.map((item: any) => (item.id === editedItem.id ? editedItem : item))
      : [...editItems, editedItem];

    props.setProp("WellnessEMSequence.wellnessPPHDList", {
      ...props.WellnessEMSequence?.wellnessPPHDList,
      items: updatedItems,
      editItems: updatedEditItems,
    });
  };

  const handleChangePatientCoverageDate = (
    key: string,
    index1: number,
    index2: number,
    date: any,
  ) => {
    const items = props.WellnessEMSequence?.wellnessPPHDList?.items || [];

    const updatedItems = items.map((item: any, i: number) =>
      i === index1
        ? {
            ...item,
            patient_coverage_max_reimbs: (item.patient_coverage_max_reimbs || []).map(
              (coverage: any, j: number) =>
                j === index2 ? { ...coverage, [key]: date } : coverage,
            ),
          }
        : item,
    );

    updateEditedItems(updatedItems, index1);
  };

  const handleChangePatientCoverageData = (index1: number, index2: number, data: any) => {
    const items = props.WellnessEMSequence?.wellnessPPHDList?.items || [];
    let value = data.checked === undefined ? data.value : data.checked;

    if (data.name === "max_reimb") {
      value = String(data.value ?? "").replace(/\D/g, "");
    }

    const updatedItems = items.map((item: any, i: number) =>
      i === index1
        ? {
            ...item,
            patient_coverage_max_reimbs: (item.patient_coverage_max_reimbs || []).map(
              (coverage: any, j: number) =>
                j === index2 ? { ...coverage, [data.name]: value } : coverage,
            ),
          }
        : item,
    );

    updateEditedItems(updatedItems, index1);
  };

  const handleChangeWellnessPPHDData = (index1: number, data: any) => {
    const items = props.WellnessEMSequence?.wellnessPPHDList?.items || [];
    const value = data.checked === undefined ? data.value : data.checked;

    const updatedItems = items.map((item: any, i: number) =>
      i === index1
        ? {
            ...item,
            [data.name]: value,
          }
        : item,
    );

    updateEditedItems(updatedItems, index1);
  };

  const handleDeletePatientCoverageData = (index1: number, index2: number, data: any) => {
    const items = props.WellnessEMSequence?.wellnessPPHDList?.items || [];

    const updatedItems = items.map((item: any, i: number) => {
      if (i !== index1) return item;

      const coverages = item.patient_coverage_max_reimbs || [];

      // ไม่มี id: ลบแถวออกทันที
      if (!data?.id) {
        return {
          ...item,
          patient_coverage_max_reimbs: coverages.filter((_: any, j: number) => j !== index2),
        };
      }

      // มี id: สลับสถานะ active
      return {
        ...item,
        patient_coverage_max_reimbs: coverages.map((coverage: any, j: number) =>
          j === index2 ? { ...coverage, active: !coverage.active } : coverage,
        ),
      };
    });

    updateEditedItems(updatedItems, index1);
  };

  const wellnessPPHDData = useMemo(() => {
    const items = props.WellnessEMSequence?.wellnessPPHDList?.items || [];

    return items.map((item: any, parentIndex: number) => {
      const divisionHN = (item?.hn || "").split("_").pop();
      const birthDate = adToBe(item?.birth_date, "YYYY-MM-DD");

      return {
        ...item,
        hn: <div style={{ display: "flex", justifyContent: "center" }}>{divisionHN}</div>,
        user_id: <div style={{ display: "flex", justifyContent: "center" }}>{item?.user_id}</div>,
        birth_date: <div style={{ display: "flex", justifyContent: "center" }}>{birthDate}</div>,
        id_card: <div style={{ display: "flex", justifyContent: "center" }}>{item?.id_card}</div>,
        is_verified: (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Checkbox
              name="is_verified"
              checked={item?.is_verified}
              onChange={(e: SyntheticEvent, data: any) =>
                handleChangeWellnessPPHDData(parentIndex, data)
              }
            />
          </div>
        ),
        patient_coverage_max_reimbs: (item.patient_coverage_max_reimbs || []).map(
          (coverage: any, coverageIndex: number) => ({
            ...coverage,
            parentIndex,
            coverageIndex,
            start_date: formatDateDashToSlash(coverage?.start_date),
            stop_date: formatDateDashToSlash(coverage?.stop_date),
          }),
        ),
      };
    });
  }, [props.WellnessEMSequence?.wellnessPPHDList?.items]);

  const columnsB = [
    {
      Header: "วันที่เริ่มต้น (ค.ศ.)",
      accessor: "start_date",
      Cell: (row: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <DateTextBox
            size="mini"
            placeholder="DD/MM/YYYY"
            adFormat="YYYY-MM-DD"
            autoAdBe
            useAdDate
            value={row.original?.start_date ?? ""}
            onChange={(date: any) =>
              handleChangePatientCoverageDate(
                "start_date",
                row.original.parentIndex,
                row.original.coverageIndex,
                date,
              )
            }
          />
        </div>
      ),
    },
    {
      Header: "วันที่สิ้นสุด (ค.ศ.)",
      accessor: "stop_date",
      Cell: (row: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <DateTextBox
            size="mini"
            placeholder="DD/MM/YYYY"
            adFormat="YYYY-MM-DD"
            autoAdBe
            useAdDate
            value={row.original?.stop_date ?? ""}
            onChange={(date: any) =>
              handleChangePatientCoverageDate(
                "stop_date",
                row.original.parentIndex,
                row.original.coverageIndex,
                date,
              )
            }
          />
        </div>
      ),
    },
    {
      Header: "ยอดใช้ไป (บาท)",
      accessor: "used_amount",
      Cell: (row: any) => (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            margin: "0px 10px",
          }}
        >
          {row.original?.used_amount}
        </div>
      ),
    },
    {
      Header: "วงเงินทั้งหมด (บาท)",
      accessor: "max_reimb",
      Cell: (row: any) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Input
            size="mini"
            name="max_reimb"
            placeholder="ระบุจำนวนวงเงิน"
            value={row.original?.max_reimb ?? ""}
            onChange={(e: SyntheticEvent, data: any) =>
              handleChangePatientCoverageData(
                row.original.parentIndex,
                row.original.coverageIndex,
                data,
              )
            }
          />
        </div>
      ),
    },
    {
      Header: "จัดการ",
      accessor: "active",
      Cell: (row: any) => {
        const hasUsedAmount = !!row.original.id && Number(row.original.used_amount) > 0;

        const deleteIcon = (
          <Icon
            className="trash"
            onClick={() =>
              handleDeletePatientCoverageData(
                row.original.parentIndex,
                row.original.coverageIndex,
                row.original,
              )
            }
            style={{
              color: hasUsedAmount ? "lightgray" : row.original.active ? "gray" : "red",
              cursor: hasUsedAmount ? "not-allowed" : "pointer",
              pointerEvents: hasUsedAmount ? "none" : "auto",
            }}
          />
        );

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {hasUsedAmount ? (
              <Popup
                content="ไม่สามารถลบได้เนื่องจากมีการใช้วงเงินไปแล้ว"
                trigger={<span>{deleteIcon}</span>}
                position="top center"
                size="tiny"
              />
            ) : (
              deleteIcon
            )}
          </div>
        );
      },
    },
  ];

  const handleSearchPPHD = () => {
    props.runSequence({
      sequence: "WellnessEmployeeManagement",
      action: "SEARCH",
      card: CARD_WELLNESS_EMPLOYEE_MANAGEMENT,
    });

    setExpandedRows([]);
  };

  const handleClearSearch = () => {
    props.setProp("WellnessEMSequence.filterWellnessEM", {
      name: "",
      hn: "",
      citizen_passport: "",
      user_id: "",
      is_verified: "ALL",
    });
  };

  const handleSavePPHD = () => {
    props.runSequence({
      sequence: "WellnessEmployeeManagement",
      action: "SAVE",
      card: CARD_WELLNESS_EMPLOYEE_MANAGEMENT,
      onCallBack: () => {
        setExpandedRows([]);
      },
    });
  };

  const handleChangePage = (activePage: number) => {
    props.runSequence({
      sequence: "WellnessEmployeeManagement",
      action: "SEARCH",
      card: CARD_WELLNESS_EMPLOYEE_MANAGEMENT,
      activePage,
    });

    setExpandedRows([]);
  };

  const handleChangeFilterData = async (e: any, data: any) => {
    const name = data?.name || e.target.name;
    const value = data?.value ?? e.target.value;

    props.setProp(`WellnessEMSequence.filterWellnessEM.${name}`, value);
  };

  const toggleRow = (id: number) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  };

  const handleAddPatientCoverage = (data: any, index: number) => {
    const newData = {
      start_date: "",
      stop_date: "",
      max_reimb: "",
      active: true,
    };

    const items = props.WellnessEMSequence?.wellnessPPHDList?.items || [];
    const updatedItems = items.map((item: any, i: number) =>
      i === index
        ? {
            ...item,
            patient_coverage_max_reimbs: [...(item.patient_coverage_max_reimbs || []), newData],
          }
        : item,
    );

    props.setProp("WellnessEMSequence.wellnessPPHDList.items", updatedItems);
  };

  return (
    <div
      className="card-wellness-em"
      style={{
        padding: "15px 10px",
        height: "calc(-3rem + 105vh)",
        overflow: "auto",
        paddingBottom: "3rem",
      }}
    >
      <SnackMessage
        error={props.errorMessage?.[CARD_WELLNESS_EMPLOYEE_MANAGEMENT]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${CARD_WELLNESS_EMPLOYEE_MANAGEMENT}`, null);
        }}
      />
      <div
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "space-between",
          padding: "0px 5px 10px",
        }}
      >
        <div style={{ fontSize: "1.15rem", fontWeight: "bold" }}>จัดการพนักงาน</div>
      </div>

      <div>
        <Form>
          <FormGroup>
            <FormField
              inline={true}
              style={{
                display: "flex",
                border: "1px solid rgba(117, 117, 117, 0.5)",
                borderRadius: "6px",
                padding: "12px 15px 0px",
                margin: "0px 10px",
              }}
              width={16}
            >
              <div style={{ width: "100%" }}>
                <Form>
                  <FormGroup>
                    <FormField width="4">
                      <div style={{ display: "flex", gap: "5px" }}>
                        <label style={{ fontWeight: "bold" }}>ชื่อ</label>
                      </div>
                      <Input
                        icon="user"
                        iconPosition="left"
                        name="name"
                        onChange={handleChangeFilterData}
                        placeholder="ชื่อ หรือ นามสกุล"
                        value={props.WellnessEMSequence?.filterWellnessEM?.name}
                      ></Input>
                    </FormField>
                    <FormField width="3">
                      <div style={{ display: "flex", gap: "5px" }}>
                        <label style={{ fontWeight: "bold" }}>HN</label>
                      </div>
                      <Input
                        name="hn"
                        onChange={handleChangeFilterData}
                        placeholder="เช่น 26010015"
                        value={props.WellnessEMSequence?.filterWellnessEM?.hn}
                      ></Input>
                    </FormField>
                    <FormField width="4">
                      <div style={{ display: "flex", gap: "5px" }}>
                        <label style={{ fontWeight: "bold" }}>เลขที่เอกสารระบุตัวตน</label>
                      </div>
                      <Input
                        icon="address card"
                        iconPosition="left"
                        name="citizen_passport"
                        onChange={handleChangeFilterData}
                        placeholder="บัตรประชาชน หรือ Passport"
                        value={props.WellnessEMSequence?.filterWellnessEM?.citizen_passport}
                      ></Input>
                    </FormField>
                    <FormField width="3">
                      <div style={{ display: "flex", gap: "5px" }}>
                        <label style={{ fontWeight: "bold" }}>User ID</label>
                      </div>
                      <Input
                        icon="sort numeric down"
                        iconPosition="left"
                        name="user_id"
                        onChange={handleChangeFilterData}
                        placeholder="เช่น 1001"
                        value={props.WellnessEMSequence?.filterWellnessEM?.user_id}
                      ></Input>
                    </FormField>
                    <FormField width="3">
                      <div style={{ display: "flex", gap: "5px" }}>
                        <label style={{ fontWeight: "bold" }}>สถานะพนักงาน</label>
                      </div>
                      <Dropdown
                        placeholder="เลือกสถานะ"
                        selection
                        clearable
                        options={verifiedOptions}
                        value={props.WellnessEMSequence?.filterWellnessEM?.is_verified}
                        name="is_verified"
                        onChange={handleChangeFilterData}
                      />
                    </FormField>
                    <FormField width="4">
                      <div style={{ display: "flex", gap: "5px" }}>
                        <label style={{ fontWeight: "bold" }}>{"\u00a0"}</label>
                      </div>
                      <div style={{ display: "flex", gap: "5px" }}>
                        <ButtonLoadCheck
                          setProp={props.setProp}
                          onClick={handleSearchPPHD}
                          paramKey={`${CARD_WELLNESS_EMPLOYEE_MANAGEMENT}_SEARCH`}
                          buttonLoadCheck={
                            props.buttonLoadCheck?.[`${CARD_WELLNESS_EMPLOYEE_MANAGEMENT}_SEARCH`]
                          }
                          style={{
                            background: "var(--primary-theme-color)",
                            color: "white",
                          }}
                          icon="search"
                          size="small"
                          title="ค้นหา"
                        />
                        <ButtonLoadCheck
                          setProp={props.setProp}
                          onClick={handleClearSearch}
                          style={{
                            backgroundColor: "#fff",
                            border: "1px solid rgba(117, 117, 117, 0.5)",
                          }}
                          icon="undo"
                          size="small"
                          title="ล้างค่า"
                        />
                      </div>
                    </FormField>
                  </FormGroup>
                </Form>
              </div>
            </FormField>
          </FormGroup>
        </Form>
      </div>
      <div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: tableWidths
              .map((w) => (typeof w === "number" ? `${w}px` : w))
              .join(" "),
            padding: "10px 0",
            background: "var(--primary-theme-color, #2f3e53)",
            color: "white",
            fontWeight: "bold",
            alignItems: "center",
          }}
        >
          <div></div>
          <div style={{ display: "flex", justifyContent: "center" }}>HN</div>
          <div style={{ display: "flex", justifyContent: "center" }}>ชื่อ</div>
          <div style={{ display: "flex", justifyContent: "center" }}>USER ID</div>
          <div style={{ display: "flex", justifyContent: "center" }}>วันเกิด</div>
          <div style={{ display: "flex", justifyContent: "center" }}>เลขที่เอกสารระบุตัวตน</div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            วงเงินปัจจุบัน (ใช้ไป / ทั้งหมด)
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>ยืนยันสถานะพนักงาน</div>
        </div>

        {Array.from({ length: Math.max(10, wellnessPPHDData.length) }, (_, index) => {
          const data = wellnessPPHDData[index];

          if (!data) {
            const emptyData = columnsA.reduce(
              (acc, column) => {
                if (typeof column.accessor === "string") {
                  acc[column.accessor] = "\u00a0";
                }
                return acc;
              },
              {} as Record<string, any>,
            );

            return (
              <div key={`empty-row-${index}`}>
                <div style={{ width: "100%" }}>
                  <Table
                    data={[emptyData]}
                    columns={columnsA}
                    defaultPageSize={1}
                    showPagination={false}
                    minRows={0}
                    getTheadProps={() => ({
                      style: { display: "none" },
                    })}
                  />
                </div>
              </div>
            );
          }

          const expanded = expandedRows.includes(data.id);

          return (
            <div key={data.id}>
              <div style={{ position: "relative" }}>
                <Button
                  onClick={() => toggleRow(data.id)}
                  style={{
                    position: "absolute",
                    zIndex: 2,
                    border: 0,
                    background: "transparent",
                    cursor: "pointer",
                  }}
                >
                  {expanded ? "▼" : "▶"}
                </Button>

                <Table
                  data={[data]}
                  columns={columnsA}
                  defaultPageSize={1}
                  showPagination={false}
                  minRows={0}
                  getTheadProps={() => ({
                    style: { display: "none" },
                  })}
                />
              </div>

              {expanded && (
                <div
                  style={{
                    background: "#e5e7eb",
                    border: "1px solid #ddd",
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: "6px",
                      margin: "10px 10px 10px 50px",
                      padding: "12px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: "1px solid rgba(34,36,38,.15)",
                      }}
                    >
                      <div style={{ display: "flex", gap: 10, fontWeight: "bold" }}>
                        <Icon
                          className="undo"
                          style={{ color: "var(--primary-theme-color-dark)" }}
                        />
                        <label>{`รายการกำหนดช่วงวงเงิน - ${data?.name}`}</label>
                      </div>

                      <div style={{ paddingBottom: "5px" }}>
                        <ButtonLoadCheck
                          setProp={props.setProp}
                          onClick={() => handleAddPatientCoverage(data, index)}
                          style={{
                            background: "#D9FCF6",
                            color: "#63CA99",
                            border: "1px solid #63CA99",
                          }}
                          icon="plus"
                          size="mini"
                          title="เพิ่มช่วงวงเงิน"
                        />
                      </div>
                    </div>

                    <div style={{ marginTop: "10px" }}>
                      <Table
                        className="patient-coverage-max-reimbs-list"
                        data={data?.patient_coverage_max_reimbs}
                        columns={columnsB}
                        defaultPageSize={5}
                        showPagination={false}
                        minRows={3}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 0px",
        }}
      >
        <div>
          {`แสดง ${((props?.WellnessEMSequence?.wellnessPPHDList?.activePage || 1) - 1) * DEFAULT_LIST_LIMIT + 1} ถึง ${Math.min((props?.WellnessEMSequence?.wellnessPPHDList?.activePage || 1) * DEFAULT_LIST_LIMIT, props?.WellnessEMSequence?.wellnessPPHDList?.total || 0)} จากทั้งหมด ${props?.WellnessEMSequence?.wellnessPPHDList?.total || 0} รายการ`}
        </div>
        <div>
          <PaginationShorthand
            activePage={props?.WellnessEMSequence?.wellnessPPHDList?.activePage || 1}
            limit={DEFAULT_LIST_LIMIT}
            total={props?.WellnessEMSequence?.wellnessPPHDList?.total || 1}
            onPageChange={handleChangePage}
          />
        </div>
        <div>
          <ButtonLoadCheck
            setProp={props.setProp}
            onClick={handleSavePPHD}
            paramKey={`${CARD_WELLNESS_EMPLOYEE_MANAGEMENT}_SAVE`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_WELLNESS_EMPLOYEE_MANAGEMENT}_SAVE`]}
            style={{
              background: "var(--primary-theme-color)",
              color: "white",
            }}
            icon="save"
            size="small"
            title="บันทึก"
          />
        </div>
      </div>
    </div>
  );
};

CardWellnessEmployeeManagement.displayName = "CardWellnessEmployeeManagement";

export default React.memo(CardWellnessEmployeeManagement);

const tableWidths = [50, 120, 200, 100, 120, 150, "auto", 180];

const columnsA = [
  {
    Header: "",
    accessor: "expand",
    width: tableWidths[0],
    Cell: () => null,
  },
  { Header: "HN", accessor: "hn", width: tableWidths[1] },
  { Header: "ชื่อ", accessor: "name", width: tableWidths[2] },
  { Header: "USER ID", accessor: "user_id", width: tableWidths[3] },
  { Header: "วันเกิด", accessor: "birth_date", width: tableWidths[4] },
  {
    Header: "เลขที่เอกสารระบุตัวตน",
    accessor: "id_card",
    width: tableWidths[5],
  },
  {
    Header: "วงเงินปัจจุบัน (ใช้ไป / ทั้งหมด)",
    accessor: "patient_coverage",
    width: tableWidths[6],
  },
  {
    Header: "ยืนยันสถานะพนักงาน",
    accessor: "is_verified",
    width: tableWidths[7],
  },
];

const verifiedOptions = [
  { key: "ALL", text: "ทั้งหมด", value: "ALL" },
  { key: "TRUE", text: "ยืนยัน", value: true },
  { key: "FALSE", text: "ไม่ยืนยัน", value: false },
];
